import { apiClient } from './client';
import type { ApiSuccess, PaginationMeta } from '../types';

export type SiteOnboardingStatus =
  | 'draft' | 'survey_pending' | 'survey_completed' | 'boq_pending' | 'boq_approved' | 'material_pending'
  | 'installation_in_progress' | 'testing_in_progress' | 'commissioning_pending' | 'handover_pending'
  | 'completed' | 'on_hold' | 'cancelled';

export const SITE_TYPES = ['new_site', 'existing_site', 'upgrade_site', 'relocation_site', 'service_site', 'amc_site'] as const;
export const SITE_REGIONS = ['north', 'south', 'east', 'west', 'central'] as const;
export const SITE_PROJECT_TYPES = ['ev_infrastructure', 'evcpd', 'om', 'amc', 'service'] as const;
export const SITE_CHARGER_TYPES = ['ac', 'dc', 'battery_swapping'] as const;
export const SITE_PRIORITIES = ['critical', 'high', 'medium', 'low'] as const;
export const SITE_DOC_TYPES = ['site_photos', 'survey_report', 'approved_boq', 'drawings', 'layout', 'site_acceptance_form', 'handover_document'] as const;
/** Section 9 lifecycle statuses selectable after submit (excludes draft). */
export const SITE_LIFECYCLE_STATUSES: SiteOnboardingStatus[] = [
  'survey_pending', 'survey_completed', 'boq_pending', 'boq_approved', 'material_pending',
  'installation_in_progress', 'testing_in_progress', 'commissioning_pending', 'handover_pending', 'completed', 'on_hold', 'cancelled',
];

export interface SiteDocument { id?: number; document_type: string; file_name?: string | null; file_url?: string | null }

export interface SiteApplication {
  id: number;
  application_code: string;
  site_id: number | null;
  status: SiteOnboardingStatus;
  project_id: number | null;
  client_id: number | null;
  site_name: string;
  site_type: string | null;
  site_address: string | null;
  landmark: string | null;
  state_id: number | null;
  city: string | null;
  district: string | null;
  pincode: string | null;
  region: string | null;
  zone: string | null;
  latitude: number | null;
  longitude: number | null;
  google_map_url: string | null;
  contact_person: string | null;
  contact_designation: string | null;
  contact_mobile: string | null;
  contact_alternate_mobile: string | null;
  contact_email: string | null;
  escalation_person: string | null;
  escalation_mobile: string | null;
  allocation_date: string | null;
  survey_planned_date: string | null;
  survey_completion_date: string | null;
  client_committed_handover_date: string | null;
  gigforce_committed_date: string | null;
  actual_handover_date: string | null;
  delay_days: number;
  oem: string | null;
  cpo: string | null;
  project_type: string | null;
  charger_type: string | null;
  charger_capacity: string | null;
  number_of_chargers: number | null;
  priority: string | null;
  project_manager_id: number | null;
  regional_ops_manager_id: number | null;
  state_asm_id: number | null;
  site_engineer_id: number | null;
  assigned_vendor_id: number | null;
  assigned_technician_id: number | null;
  source_type: string | null;
  submitted_at: string | null;
  completed_steps: number[];
  documents: SiteDocument[];
  project_code?: string | null;
  project_name?: string | null;
  client_name?: string | null;
}

export const siteOnboardingApi = {
  async get(id: number): Promise<SiteApplication> {
    return (await apiClient.get<ApiSuccess<SiteApplication>>(`/site-onboarding/${id}`)).data.data;
  },
  async create(payload: Record<string, unknown>): Promise<SiteApplication> {
    return (await apiClient.post<ApiSuccess<SiteApplication>>('/site-onboarding', payload)).data.data;
  },
  async saveDraft(id: number, payload: Record<string, unknown>): Promise<SiteApplication> {
    return (await apiClient.put<ApiSuccess<SiteApplication>>(`/site-onboarding/${id}/draft`, payload)).data.data;
  },
  async submit(id: number): Promise<SiteApplication> {
    return (await apiClient.post<ApiSuccess<SiteApplication>>(`/site-onboarding/${id}/submit`, {})).data.data;
  },
  async list(params: Record<string, unknown> = {}): Promise<{ data: SiteApplication[]; meta: PaginationMeta }> {
    const { data } = await apiClient.get<ApiSuccess<SiteApplication[]>>('/site-onboarding', { params });
    return { data: data.data, meta: data.meta as PaginationMeta };
  },
  async changeStatus(id: number, status: string): Promise<SiteApplication> {
    return (await apiClient.post<ApiSuccess<SiteApplication>>(`/site-onboarding/${id}/status`, { status })).data.data;
  },
  async cancel(id: number): Promise<SiteApplication> {
    return (await apiClient.post<ApiSuccess<SiteApplication>>(`/site-onboarding/${id}/cancel`, {})).data.data;
  },
  async reopen(id: number): Promise<SiteApplication> {
    return (await apiClient.post<ApiSuccess<SiteApplication>>(`/site-onboarding/${id}/reopen`, {})).data.data;
  },
  async remove(id: number): Promise<{ deleted: true }> {
    return (await apiClient.delete<ApiSuccess<{ deleted: true }>>(`/site-onboarding/${id}`)).data.data;
  },
  async clone(id: number): Promise<SiteApplication> {
    return (await apiClient.post<ApiSuccess<SiteApplication>>(`/site-onboarding/${id}/clone`, {})).data.data;
  },
};

export interface SiteOnboardingDashboard {
  kpis: Record<string, number>;
  charts: Record<string, Array<{ label: string; value: number }>>;
  tables: { recentSites: Array<Record<string, unknown>>; delayedSites: Array<Record<string, unknown>> };
}

export const siteDashboardApi = {
  async dashboard(): Promise<SiteOnboardingDashboard> {
    return (await apiClient.get<ApiSuccess<SiteOnboardingDashboard>>('/site-onboarding-dashboard')).data.data;
  },
};
